/**
 * ActivityCategoryPicker — vertical category list shared between
 * `AddActivityModal` and `BulkImportModal` so both screen 1 surfaces look
 * identical. Renders `OTHER_CATEGORIES` (Scope 1 + Scope 2) as grouped rows
 * with an icon, label, scope badge, description and an Add button.
 */

import { Plus } from 'lucide-react';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { type EmissionScope } from '../../../types/emissions';
import {
  SCOPE1_ACTIVITY_TYPES,
  SCOPE2_ACTIVITY_TYPES,
} from '../types';
import {
  type ActivityCategory,
  type SuggestedActivity,
} from '../AssetActivitiesStep';

interface ActivityCategoryPickerProps {
  categories: ActivityCategory[];
  /** Label for the row's primary action button (e.g. "Add", "Choose"). */
  actionLabel?: string;
  onSelect: (suggested: SuggestedActivity) => void;
}

export const ActivityCategoryPicker = ({
  categories,
  actionLabel = 'Add',
  onSelect,
}: ActivityCategoryPickerProps) => (
  <div className='tw:space-y-6'>
    {categories.map((cat, catIndex) => (
      <div key={cat.label}>
        <div className='tw:flex tw:items-center tw:gap-2 tw:mb-1'>
          <span className='tw:text-sm tw:font-semibold'>{cat.label}</span>
        </div>
        <p className='tw:text-xs tw:text-muted-foreground tw:mb-3'>{cat.description}</p>
        <div className='tw:space-y-0'>
          {cat.activities.map((suggested, sugIdx) => {
            const brand = [...SCOPE1_ACTIVITY_TYPES, ...SCOPE2_ACTIVITY_TYPES].find(
              (b) => b.value === suggested.activityType,
            );
            const IconComponent = suggested.Icon ?? brand?.Icon;
            const iconBgClass = suggested.iconBg ?? brand?.iconBg;
            const iconColorClass = suggested.iconColor ?? brand?.iconColor;
            const scopeLabel = suggested.scope as EmissionScope;
            return (
              <div
                key={`${suggested.label}-${sugIdx}`}
                className='tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-3 tw:rounded-lg tw:hover:bg-muted/50 tw:transition-colors'
              >
                <div className='tw:flex tw:items-center tw:gap-3'>
                  {IconComponent && (
                    <div
                      className={cn(
                        'tw:rounded-full tw:shrink-0 tw:flex tw:items-center tw:justify-center tw:size-7',
                        iconBgClass,
                      )}
                    >
                      <IconComponent className={cn('tw:size-3.5', iconColorClass)} />
                    </div>
                  )}
                  <div>
                    <div className='tw:text-sm tw:font-medium tw:flex tw:items-center tw:gap-2'>
                      {suggested.label}
                      <Badge variant='secondary' className='tw:text-[10px] tw:font-normal'>
                        {scopeLabel === 'scope1' ? 'Scope 1' : scopeLabel === 'scope2' ? 'Scope 2' : 'Scope 3'}
                      </Badge>
                    </div>
                    <p className='tw:text-xs tw:text-muted-foreground tw:mt-0.5 tw:mb-0'>{suggested.description}</p>
                  </div>
                </div>
                <Button
                  size='sm'
                  className='tw:bg-emerald-700 tw:hover:bg-emerald-800 tw:text-white tw:border-0 tw:shrink-0'
                  onClick={() => onSelect(suggested)}
                >
                  <Plus className='tw:size-3.5 tw:mr-1' />
                  {actionLabel}
                </Button>
              </div>
            );
          })}
        </div>
        {catIndex < categories.length - 1 && <div className='tw:border-b tw:border-border tw:mt-4' />}
      </div>
    ))}
  </div>
);
